import { ImageState, ManualEditState } from "@/api/images-types";
import { SubmissionState } from "@/api/submission-types";
import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { ImageBreadcrumbs } from "@/components/ui/breadcrumbs/ImageBreadcrumbs";
import { TimestampBreadcrumbs } from "@/components/ui/breadcrumbs/TimestampBreadcrumbs";
import { Link } from "@/components/ui/Link";
import { BoxLoader } from "@/components/ui/loaders";
import { ImageStateChip } from "@/components/ui/state-chips/ImageStateChip";
import { SubmissionStateChip } from "@/components/ui/state-chips/SubmissionStateChip";
import { TagsRow } from "@/components/ui/tags/TagsRow";
import { IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT } from "@/const";
import { hasRole } from "@/hooks/use-has-role";
import { useUser } from "@/queries/auth";
import { useEpisodeImagesByEpisodeId } from "@/queries/episodes/use-episode-images-by-episode-id";
import { useEpisodeTextsByEpisodeId } from "@/queries/episodes/use-episode-texts-by-episode-id";
import { useImageById } from "@/queries/images/use-image-by-id";
import { Image } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  darken,
  Grid2,
  List,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { blue, green, grey, red } from "@mui/material/colors";
import { useNavigate } from "react-router";
import { AssetGenerationMetadata } from "./AssetGenerationMetadata";

type Props = {
  backButton?: boolean;
  id: string;
  mode: "browse" | "validate";
  onModeChange?: (mode: "browse" | "validate") => void;
  isChildPresent?: boolean;
  onToggleCharacter?: () => void;
  onRejectWholeEpisode?: () => void;
};

export function ImageDetails({
  backButton,
  id,
  mode,
  onModeChange,
  isChildPresent,
  onToggleCharacter,
  onRejectWholeEpisode,
}: Props) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useImageById(id);
  const { data: episodeImages } = useEpisodeImagesByEpisodeId(data?.episode.id);
  const { data: episodeTexts } = useEpisodeTextsByEpisodeId(data?.episode.id);

  const { data: user } = useUser();

  const isCheckboxDisabled =
    mode === "browse" || data?.submissionState !== SubmissionState.unsubmitted;

  function renderData() {
    if (!data) {
      return null;
    }
    const {
      page,
      pageText,
      prompt,
      rejectionReasons,
      submissionState,
      state,
      url,
      episodeTitle,
      episode,
      previousImagesCount,
      needsManualEdit,
      manualEditState,
    } = data;

    const sortedImages = (episodeImages?.images || []).toSorted(
      (a, b) => a.page - b.page,
    );

    return (
      <>
        <EpisodeBreadcrumbs
          id={episode.id}
          category={episode.category}
          gender={episode.gender}
          story={episode.story}
          subcategory={episode.subcategory}
          backToEpisodePageTab={backButton ? "images" : undefined}
          linkToEpisodePage={!backButton}
        />
        <Typography component={"h1"} variant={"h5"}>
          {episodeTitle}
        </Typography>
        <TagsRow episodeId={episode.id} />
        <ImageBreadcrumbs id={id} />
        <TimestampBreadcrumbs {...data} />
        {mode === "browse" && <AssetGenerationMetadata {...data} />}
        <Link to={`/episodes/${episode.id}`} state={{ tab: "images" }}>
          Story summary
        </Link>
        {mode === "browse" && (
          <>
            <Stack direction={"row"} gap={1}>
              <SubmissionStateChip size={"medium"} state={submissionState} />
              <ImageStateChip size={"medium"} state={state} />
              {submissionState === SubmissionState.unsubmitted &&
                [ImageState.accepted, ImageState.rejected].includes(state) &&
                user?.id === episode.visualsAssignee?.id &&
                episode.submissionState === SubmissionState.unsubmitted && (
                  <Button
                    variant="outlined"
                    onClick={() => onModeChange && onModeChange("validate")}
                    sx={{ height: 32 }}
                  >
                    {"Validate again"}
                  </Button>
                )}
            </Stack>
            {state === ImageState.rejected && !!rejectionReasons?.length && (
              <Box>
                <Typography variant={"body1"} color={"textSecondary"}>
                  Rejection reasons:
                </Typography>
                <List sx={{ listStyle: "initial", pl: 2, pb: 0 }}>
                  {rejectionReasons.map((reason) => (
                    <li key={reason}>
                      <Typography color={"textSecondary"}>{reason}</Typography>
                    </li>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
        {mode === "validate" && onModeChange && (
          <Stack direction={"row"} gap={1}>
            <Button
              variant="outlined"
              onClick={() => onModeChange("browse")}
              sx={{ height: 32 }}
            >
              {"Back to browse"}
            </Button>
          </Stack>
        )}

        {previousImagesCount > 0 &&
          manualEditState !== ManualEditState.fixed && (
            <Typography color="error">This is a regenerated image</Typography>
          )}
        {needsManualEdit && (
          <Typography color="warning.main" fontWeight={700}>
            {`✏️ This image needs manual editing (rejected ${IMAGE_REJECTION_COUNT_BEFORE_MANUAL_EDIT}+ times)`}
          </Typography>
        )}
        {manualEditState === ManualEditState.fixed && (
          <Typography color="success.main" fontWeight={700}>
            {"This image has been manually edited"}
          </Typography>
        )}
        <Box
          sx={[
            {
              width: "100%",
              borderRadius: (theme) => theme.shape.borderRadius,
              backgroundColor:
                mode === "browse"
                  ? state === ImageState.accepted
                    ? green[50]
                    : state === ImageState.rejected
                      ? red[50]
                      : "background.paper"
                  : "background.paper",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            },
            (theme) =>
              theme.applyStyles("dark", {
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor:
                  mode === "browse"
                    ? state === ImageState.accepted
                      ? darken(green[900], 0.2)
                      : state === ImageState.rejected
                        ? darken(red[900], 0.2)
                        : "background.paper"
                    : "background.paper",
              }),
          ]}
        >
          {url ? (
            <img src={url} style={{ width: "1000px" }} />
          ) : (
            <Stack
              gap={2}
              alignItems={"center"}
              justifyContent={"center"}
              height={350}
            >
              <Image sx={{ fontSize: "60px", color: grey[400] }} />
              <Typography variant="h6">Missing image url!</Typography>
            </Stack>
          )}
        </Box>
        {url && (
          <Stack
            gap={0.5}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Typography variant={"h5"} color="textSecondary">
              Is user character present?
            </Typography>
            <Checkbox
              checked={isChildPresent ?? false}
              disabled={isCheckboxDisabled}
              size="large"
              onChange={onToggleCharacter}
            />
          </Stack>
        )}
        <Box>
          <Typography
            variant="body2"
            color={"textSecondary"}
          >{`Page ${page}`}</Typography>
          <Typography component={"p"}>{pageText}</Typography>
        </Box>

        {user && hasRole(user, RoleName.episodeManager) && (
          <Box>
            <Typography
              variant="body2"
              color={"textSecondary"}
            >{`Prompt`}</Typography>
            <Typography component={"p"}>{prompt}</Typography>
          </Box>
        )}

        {sortedImages.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">
                All Pages ({sortedImages.length})
              </Typography>
              {onRejectWholeEpisode && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={onRejectWholeEpisode}
                >
                  {"Reject whole story"}
                </Button>
              )}
            </Stack>
            <Grid2 container gap={2}>
              {sortedImages.map((image) => {
                const isCurrentImage = image.id === id;
                const getBorderColor = () => {
                  if (isCurrentImage) return blue[500];
                  if (image.state === ImageState.accepted) return green[500];
                  if (image.state === ImageState.rejected) return red[500];
                  return grey[500];
                };

                const pageText =
                  episodeTexts?.pages.find(
                    (page) => page.pageNumber === image.page,
                  )?.text || "No text available";

                return (
                  <Grid2 size={{ xs: 3 }} key={image.id}>
                    <Tooltip
                      arrow
                      title={
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {pageText}
                        </Typography>
                      }
                      placement="top"
                    >
                      <Box
                        onClick={() => {
                          if (mode === "validate") {
                            window.open(`/images/${image.id}`);
                          } else {
                            navigate(`/images/${image.id}`);
                          }
                        }}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          border: `6px solid ${getBorderColor()}`,
                          borderRadius: 1,
                          overflow: "hidden",
                          "&:hover": {
                            opacity: 0.8,
                            transform: "scale(1.02)",
                          },
                          transition: "all 0.2s ease-in-out",
                          minHeight: "40px",
                        }}
                      >
                        {image.url ? (
                          <img
                            src={image.url}
                            alt={`Page ${image.page}`}
                            style={{
                              width: "100%",
                              height: "auto",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box
                            height={200}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Image
                              sx={{ fontSize: "48px", color: grey[400] }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(0, 0, 0, 0.6)",
                            color: "white",
                            py: 0.5,
                            px: 1,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={isCurrentImage ? "bold" : "normal"}
                          >
                            Page {image.page}
                            {isCurrentImage && " (Current)"}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  </Grid2>
                );
              })}
            </Grid2>
          </Box>
        )}
      </>
    );
  }

  return (
    <Stack gap={1.5}>
      <ApiErrorAlert error={error} />
      <BoxLoader isLoading={isLoading} height={100} />
      {renderData()}
    </Stack>
  );
}
